import { AnySchema, object, string } from 'yup';

import { EConfigurationDataFieldNames, TConfigurationDataFieldNames } from '@interfaces/cmModule/configurationData';

import { regExpNumbersWithDots, regExpOnlyNumbers } from '@constants/regExp';
import {
	getDigitsAndDotsErrorMessage,
	getMaxLengthErrMsg,
	getOnlyDigitsErrMsg,
	mandatoryErrMessage,
} from '@constants/validationConstants';
import { MAX_20_LENGTH, MAX_50_LENGTH } from '@constants/validationMessages';
import { configurationDataFormLabels } from '@components/routes/CmModule/ConfigurationDataModelManagement/constants';

export const mainSchema = ({
	requiredFields,
}: {
	requiredFields: TConfigurationDataFieldNames[];
}) => {
	const schemaFields: { [key in TConfigurationDataFieldNames]?: AnySchema } = {
		[EConfigurationDataFieldNames.NAME]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.nullable()
					.required(mandatoryErrMessage(configurationDataFormLabels[EConfigurationDataFieldNames.NAME]))
					.max(MAX_50_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NAME],
							MAX_50_LENGTH)),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]: object()
			.nullable(),
		[EConfigurationDataFieldNames.VDU_ID]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.required(mandatoryErrMessage(configurationDataFormLabels[EConfigurationDataFieldNames.VDU_ID]))
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.VDU_ID],
							MAX_20_LENGTH))
					.matches(regExpOnlyNumbers,
						getOnlyDigitsErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.VDU_ID])),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.DATA_MODEL_TYPE]: object()
			.nullable(),
		[EConfigurationDataFieldNames.DATA_MODEL]: string()
			.trim()
			.default('')
			.max(MAX_50_LENGTH,
				getMaxLengthErrMsg(
					configurationDataFormLabels[EConfigurationDataFieldNames.DATA_MODEL], MAX_50_LENGTH,
				),
			),
		[EConfigurationDataFieldNames.DEVICE_TYPE]: object()
			.nullable(),
		[EConfigurationDataFieldNames.PNF_TYPE]: object()
			.nullable(),
		[EConfigurationDataFieldNames.BUILD_VERSION]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.BUILD_VERSION],
							MAX_20_LENGTH))
					.matches(regExpNumbersWithDots,
						getDigitsAndDotsErrorMessage(
							configurationDataFormLabels[EConfigurationDataFieldNames.BUILD_VERSION],
						)),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.ELEMENT_TYPE]: object()
			.nullable(),
		[EConfigurationDataFieldNames.GROUP_BY]: object()
			.nullable(),
		[EConfigurationDataFieldNames.PRODUCT_NAME]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_50_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.PRODUCT_NAME],
							MAX_50_LENGTH)),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.NS_ID]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NS_ID],
							MAX_20_LENGTH))
					.matches(regExpOnlyNumbers,
						getOnlyDigitsErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NS_ID])),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.NS_VERSION]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NS_VERSION],
							MAX_20_LENGTH))
					.matches(regExpNumbersWithDots,
						getDigitsAndDotsErrorMessage(
							configurationDataFormLabels[EConfigurationDataFieldNames.NS_VERSION],
						)),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.NF_TYPE]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NF_TYPE],
							MAX_20_LENGTH))
					.matches(regExpOnlyNumbers,
						getOnlyDigitsErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.NF_TYPE])),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.COMPONENT_TYPE]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_TYPE],
							MAX_20_LENGTH))
					.matches(regExpOnlyNumbers,
						getOnlyDigitsErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_TYPE])),
			}))
			.nullable(),
		[EConfigurationDataFieldNames.COMPONENT_VERSION]: object()
			.shape(({
				value: string()
					.trim()
					.default('')
					.max(MAX_20_LENGTH,
						getMaxLengthErrMsg(configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_VERSION],
							MAX_20_LENGTH))
					.matches(regExpNumbersWithDots,
						getDigitsAndDotsErrorMessage(
							configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_VERSION],
						)),
			}))
			.nullable(),
	};

	if (requiredFields) {
		requiredFields.forEach((field) => {
			if (schemaFields[field]) {
				const fieldSchema = schemaFields[field];
				if (fieldSchema) {
					schemaFields[field] = fieldSchema.required(
						mandatoryErrMessage(
							Object.hasOwn(configurationDataFormLabels, field)
								? configurationDataFormLabels[field as keyof typeof configurationDataFormLabels]
								: field,
						),
					);
				}
			}
		});
	}

	return object().shape(schemaFields);
};
